import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import {
    AlertType,
    GroupType,
    IAlarmDefinitionChild,
    IBandDefinitionChild,
    IIndicatorResponse,
    IUnitResponse,
    MetricClassificationSourceEnum,
    MetricTypeCodes,
} from '@kcftech/prometheus-client';
import { lastItem } from 'utils/array';
import { GenericHierarchyChild } from 'utils/group_hierarchy';

export type HierarchyInfo =
    | {
          type: 'prometheus';
          hierarchy: GenericHierarchyChild[];
      }
    | {
          type: 'data-platform';
          hierarchy: Omit<GenericHierarchyChild, 'HierarchyType'>[];
      };

/** the backend an indicator (and its id) belongs to */
export type IndicatorSource = HierarchyInfo['type'];

export type GeneralIndicatorInfo = Readonly<{
    Id: string;
    Name: string;
    IndicatorType?: MetricTypeCodes;
    DisplayUnits?: string;
    Statistic?: DataPlatformApi.Statistic;
    PhysicalQuantity?: string;
    PhysicalQuantityType?: string;
    HierarchyInfo: HierarchyInfo;
    AlarmDefinition?: IAlarmDefinitionChild & {
        NotificationCriteriaTime?: number;
    };
    HasSpectrum: boolean;
    HasBaseline?: boolean;
    HasArbitraryUnits?: boolean;
    IsVibration: boolean;
    IsHardwareFiltered?: boolean;
    IsMultirateLow?: boolean;
    BandDefinition?: IBandDefinitionChild;
    AlertStatus?: AlertType;
}>;

export const getMonitoringPointIdFromHierarchy = (
    hierarchyInfo: HierarchyInfo | undefined,
): string | undefined => {
    if (!hierarchyInfo) {
        return undefined;
    }

    // data platform doesn't return hierarchy type info, but they
    // don't have indicator groups, so the direct parent of the indicator is the monitoring point
    if (hierarchyInfo.type === 'data-platform') {
        return lastItem(hierarchyInfo.hierarchy)?.HierarchyId;
    }

    return hierarchyInfo.hierarchy.find(
        (hierarchyItem) => hierarchyItem.HierarchyType === GroupType.MonitoringPoint,
    )?.HierarchyId;
};

export const mapPromIndicatorToGeneralIndicatorInfo = (
    indicator: IIndicatorResponse,
): GeneralIndicatorInfo => {
    return {
        ...indicator,
        HierarchyInfo: {
            type: 'prometheus',
            hierarchy: indicator.Hierarchy,
        },
    };
};

export const mapDataPlatformAlertConfigToAlarmDefinition = (
    alertConfig: DataPlatformApi.AlertConfig | null,
): IAlarmDefinitionChild & { NotificationCriteriaTime?: number } => {
    return {
        CriticalLowAlarmValue: alertConfig?.lowCriticalAlarm ?? undefined,
        LowAlarmValue: alertConfig?.lowAlarm ?? undefined,
        LowWarningValue: alertConfig?.lowWarning ?? undefined,
        HighWarningValue: alertConfig?.highWarning ?? undefined,
        HighAlarmValue: alertConfig?.highAlarm ?? undefined,
        CriticalHighAlarmValue: alertConfig?.highCriticalAlarm ?? undefined,
        Classification: alertConfig?.off
            ? MetricClassificationSourceEnum.Self
            : MetricClassificationSourceEnum.None,
        OffThresholdValue: alertConfig?.off ?? undefined,
        NotificationCriteriaPoints: alertConfig?.minPoints ?? undefined,
        NotificationCriteriaPercentage: alertConfig?.minPointRatio
            ? alertConfig.minPointRatio * 100
            : undefined,
        // TODO: @ari when the SDK gets updated so the type is correct, the cast can be removed
        NotificationCriteriaTime: alertConfig?.alertTimespan
            ? Number(alertConfig.alertTimespan)
            : undefined,
    };
};

export const mapDataPlatformHierarchyToHierarchyChild = (
    items: DataPlatformApi.Group[],
): Omit<GenericHierarchyChild, 'HierarchyType'>[] =>
    items.map((item) => ({
        HierarchyId: item.id,
        HierarchyName: item.name,
        HierarchyAbbreviation: item.abbreviation,
    }));

export const mapDataPlatformIndicatorToGeneralIndicatorInfo = (
    indicator: DataPlatformApi.IndicatorResponse,
    unitsRequest?: Record<string, string>,
    units?: Map<string, IUnitResponse>,
): GeneralIndicatorInfo => {
    // The data platform always returns indicator units in their base units,
    // so we need to map the base unit to the user's preferred unit for display purposes.
    const unitCode = unitsRequest?.[indicator.physicalQuantity] || indicator.unit.code;
    const unit = units?.get(unitCode);

    return {
        Id: indicator.id,
        Name: indicator.name,
        HierarchyInfo: {
            type: 'data-platform',
            hierarchy: mapDataPlatformHierarchyToHierarchyChild(indicator.groupHierarchy),
        },
        AlarmDefinition: mapDataPlatformAlertConfigToAlarmDefinition(
            indicator.alertConfig,
        ),
        HasSpectrum: indicator.sourceType === DataPlatformApi.SourceType.Waveform,
        IsVibration:
            indicator.physicalQuantity === 'acceleration' ||
            indicator.physicalQuantity === 'velocity',
        Statistic: indicator.statistic,
        PhysicalQuantity: indicator.physicalQuantity,
        PhysicalQuantityType: indicator.physicalQuantityType ?? undefined,
        HasBaseline: indicator.hasBaseline,
        HasArbitraryUnits: indicator.unit.code.includes("'U"),
        DisplayUnits: unit?.Abbreviation ?? indicator.unit.abbreviation,
        BandDefinition: indicator.band
            ? {
                  HighBandFrequency: indicator.band.max,
                  LowBandFrequency: indicator.band.min,
              }
            : undefined,
        IsHardwareFiltered: indicator.filtered,
        IsMultirateLow: indicator.multirateLow,
    };
};
